import { Icon } from '@iconify/react';
import type { DragEvent } from 'react';
import type {
  SortDirection,
  SortKey,
  Student,
  StudentStatus,
} from './types';

type StudentsPanelProps = {
  data: {
    view: 'list' | 'grid';
    isDetailView: boolean;
    isLoading: boolean;
    hasError: boolean;
    students: Student[];
    sortedStudents: Student[];
    studentId?: string;
    openStatusId: string | null;
    statusUpdating: boolean;
    updatingStatusId: string | null;
    sortKey: SortKey;
    sortDirection: SortDirection;
    listGridCols: string;
    statusStyles: Record<StudentStatus, string>;
    statusOptions: ReadonlyArray<{
      value: StudentStatus;
      label: string;
      text: string;
    }>;
    statusColumns: ReadonlyArray<{
      status: StudentStatus;
      label: string;
      text: string;
      badge: string;
      panel: string;
      panelColor: string;
    }>;
    searchQuery: string;
  };
  actions: {
    onViewChange: (view: 'list' | 'grid') => void;
    onSort: (key: SortKey) => void;
    onSelectStudent: (id: string) => void;
    onOpenStatusMenu: (id: string | null) => void;
    onStatusSelect: (student: Student, status: StudentStatus) => void;
    onOpenNotes: (id: string) => void;
    onOpenResources: (id: string) => void;
    onOpenMessages: () => void;
    onDragStart: (id: string, event: DragEvent<HTMLDivElement>) => void;
    onDragOver: (event: DragEvent<HTMLDivElement>) => void;
    onDrop: (status: StudentStatus, event: DragEvent<HTMLDivElement>) => void;
    onSearchChange: (query: string) => void;
  };
};

const getStudentSummary = (text: string) => {
  const limit = 90;
  if (text.length <= limit) {
    return text;
  }
  return `${text.slice(0, limit).trim()}...`;
};

const StudentsPanel = ({ data, actions }: StudentsPanelProps) => {
  const {
    view,
    isDetailView,
    isLoading,
    hasError,
    students,
    sortedStudents,
    studentId,
    openStatusId,
    statusUpdating,
    updatingStatusId,
    listGridCols,
    statusStyles,
    statusOptions,
    statusColumns,
    searchQuery,
  } = data;
  const {
    onViewChange,
    onSort,
    onSelectStudent,
    onOpenStatusMenu,
    onStatusSelect,
    onOpenNotes,
    onOpenResources,
    onOpenMessages,
    onDragStart,
    onDragOver,
    onDrop,
    onSearchChange,
  } = actions;
  return (
    <div
      className={`flex w-full flex-col gap-6 rounded-[12px] border border-slate-200 bg-white px-4 py-4 ${
        isDetailView
          ? 'lg:w-[360px] lg:min-w-[360px] lg:max-w-[360px] lg:flex-none'
          : ''
      }`}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2">
            <button
              className="rounded-[8px] bg-slate-100 px-4 py-2 text-[16px] font-semibold text-[#667085] transition hover:bg-slate-200"
              type="button"
            >
              Active
            </button>
          </div>
          <div
            className="flex items-center gap-1 rounded-[8px] bg-slate-100 px-2 py-1"
            role="tablist"
            aria-label="Students view"
          >
            <button
              type="button"
              role="tab"
              aria-selected={view === 'list'}
              onClick={() => onViewChange('list')}
              className={`flex h-10 w-10 items-center justify-center rounded-[6px] transition ${
                view === 'list'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'bg-transparent text-slate-400 hover:text-slate-600'
              }`}
              aria-label="List view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
  <path d="M1.25 11.6667C1.58152 11.6667 1.89946 11.7984 2.13388 12.0328C2.3683 12.2672 2.5 12.5851 2.5 12.9167C2.5 13.2482 2.3683 13.5661 2.13388 13.8006C1.89946 14.035 1.58152 14.1667 1.25 14.1667C0.918479 14.1667 0.600537 14.035 0.366117 13.8006C0.131696 13.5661 0 13.2482 0 12.9167C0 12.5851 0.131696 12.2672 0.366117 12.0328C0.600537 11.7984 0.918479 11.6667 1.25 11.6667ZM14.1667 12.0833C14.3877 12.0833 14.5996 12.1711 14.7559 12.3274C14.9122 12.4837 15 12.6957 15 12.9167C15 13.1377 14.9122 13.3496 14.7559 13.5059C14.5996 13.6622 14.3877 13.75 14.1667 13.75H5C4.77899 13.75 4.56702 13.6622 4.41074 13.5059C4.25446 13.3496 4.16667 13.1377 4.16667 12.9167C4.16667 12.6957 4.25446 12.4837 4.41074 12.3274C4.56702 12.1711 4.77899 12.0833 5 12.0833H14.1667ZM1.25 5.83333C1.58152 5.83333 1.89946 5.96503 2.13388 6.19945C2.3683 6.43387 2.5 6.75181 2.5 7.08333C2.5 7.41485 2.3683 7.7328 2.13388 7.96722C1.89946 8.20164 1.58152 8.33333 1.25 8.33333C0.918479 8.33333 0.600537 8.20164 0.366117 7.96722C0.131696 7.7328 0 7.41485 0 7.08333C0 6.75181 0.131696 6.43387 0.366117 6.19945C0.600537 5.96503 0.918479 5.83333 1.25 5.83333ZM14.1667 6.25C14.3791 6.25024 14.5834 6.33157 14.7378 6.47737C14.8923 6.62318 14.9852 6.82246 14.9976 7.0345C15.0101 7.24653 14.9411 7.45532 14.8048 7.61819C14.6685 7.78107 14.4751 7.88575 14.2642 7.91083L14.1667 7.91667H5C4.7876 7.91643 4.58331 7.8351 4.42886 7.68929C4.27441 7.54349 4.18147 7.34421 4.16902 7.13217C4.15658 6.92014 4.22556 6.71135 4.36189 6.54847C4.49821 6.3856 4.69159 6.28092 4.9025 6.25583L5 6.25H14.1667ZM1.25 0C1.58152 0 1.89946 0.131696 2.13388 0.366117C2.3683 0.600537 2.5 0.918479 2.5 1.25C2.5 1.58152 2.3683 1.89946 2.13388 2.13388C1.89946 2.3683 1.58152 2.5 1.25 2.5C0.918479 2.5 0.600537 2.3683 0.366117 2.13388C0.131696 1.89946 0 1.58152 0 1.25C0 0.918479 0.131696 0.600537 0.366117 0.366117C0.600537 0.131696 0.918479 0 1.25 0ZM14.1667 0.416667C14.3791 0.416902 14.5834 0.498232 14.7378 0.64404C14.8923 0.789848 14.9852 0.989128 14.9976 1.20116C15.0101 1.4132 14.9411 1.62198 14.8048 1.78486C14.6685 1.94774 14.4751 2.05241 14.2642 2.0775L14.1667 2.08333H5C4.7876 2.0831 4.58331 2.00177 4.42886 1.85596C4.27441 1.71015 4.18147 1.51087 4.16902 1.29884C4.15658 1.0868 4.22556 0.878017 4.36189 0.71514C4.49821 0.552262 4.69159 0.447586 4.9025 0.4225L5 0.416667H14.1667Z" fill="#212121"/>
</svg>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={view === 'grid'}
              onClick={() => onViewChange('grid')}
              className={`flex h-10 w-10 items-center justify-center rounded-[6px] transition ${
                view === 'grid'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'bg-transparent text-slate-400 hover:text-slate-600'
              }`}
              aria-label="Grid view"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
  <path d="M0.5 1.66683C0.5 1.22481 0.675595 0.800872 0.988155 0.488312C1.30072 0.175752 1.72464 0.00016365 2.16667 0.00016365H5.33333C5.77536 0.00016365 6.19928 0.175752 6.51184 0.488312C6.8244 0.800872 7 1.22481 7 1.66683V4.83347C7 5.2755 6.8244 5.69942 6.51184 6.01198C6.19928 6.32454 5.77536 6.50013 5.33333 6.50013H2.16667C1.72464 6.50013 1.30072 6.32454 0.988155 6.01198C0.675595 5.69942 0.5 5.2755 0.5 4.83347V1.66683ZM2.16667 1.50016C2.12246 1.50016 2.08007 1.51782 2.04881 1.54908C2.01756 1.58034 2 1.62272 2 1.66683V4.83347C2 4.87758 2.01756 4.91996 2.04881 4.95122C2.08007 4.98248 2.12246 5.00013 2.16667 5.00013H5.33333C5.37754 5.00013 5.41993 4.98248 5.45119 4.95122C5.48244 4.91996 5.5 4.87758 5.5 4.83347V1.66683C5.5 1.62272 5.48244 1.58034 5.45119 1.54908C5.41993 1.51782 5.37754 1.50016 5.33333 1.50016H2.16667ZM0.5 12.1668C0.5 11.7248 0.675595 11.3009 0.988155 10.9883C1.30072 10.6757 1.72464 10.5002 2.16667 10.5002H5.33333C5.77536 10.5002 6.19928 10.6757 6.51184 10.9883C6.8244 11.3009 7 11.7248 7 12.1668V15.3335C7 15.7755 6.8244 16.1994 6.51184 16.512C6.19928 16.8245 5.77536 17.0001 5.33333 17.0001H2.16667C1.72464 17.0001 1.30072 16.8245 0.988155 16.512C0.675595 16.1994 0.5 15.7755 0.5 15.3335V12.1668ZM2.16667 12.0002C2.12246 12.0002 2.08007 12.0178 2.04881 12.0491C2.01756 12.0803 2 12.1227 2 12.1668V15.3335C2 15.3776 2.01756 15.42 2.04881 15.4512C2.08007 15.4825 2.12246 15.5001 2.16667 15.5001H5.33333C5.37754 15.5001 5.41993 15.4825 5.45119 15.4512C5.48244 15.42 5.5 15.3776 5.5 15.3335V12.1668C5.5 12.1227 5.48244 12.0803 5.45119 12.0491C5.41993 12.0178 5.37754 12.0002 5.33333 12.0002H2.16667ZM10.5 1.66683C10.5 1.22481 10.6756 0.800872 10.9882 0.488312C11.3007 0.175752 11.7246 0.00016365 12.1667 0.00016365H15.3333C15.7754 0.00016365 16.1993 0.175752 16.5118 0.488312C16.8244 0.800872 17 1.22481 17 1.66683V4.83347C17 5.2755 16.8244 5.69942 16.5118 6.01198C16.1993 6.32454 15.7754 6.50013 15.3333 6.50013H12.1667C11.7246 6.50013 11.3007 6.32454 10.9882 6.01198C10.6756 5.69942 10.5 5.2755 10.5 4.83347V1.66683ZM12.1667 1.50016C12.1225 1.50016 12.0801 1.51782 12.0488 1.54908C12.0176 1.58034 12 1.62272 12 1.66683V4.83347C12 4.87758 12.0176 4.91996 12.0488 4.95122C12.0801 4.98248 12.1225 5.00013 12.1667 5.00013H15.3333C15.3775 5.00013 15.4199 4.98248 15.4512 4.95122C15.4824 4.91996 15.5 4.87758 15.5 4.83347V1.66683C15.5 1.62272 15.4824 1.58034 15.4512 1.54908C15.4199 1.51782 15.3775 1.50016 15.3333 1.50016H12.1667ZM10.5 12.1668C10.5 11.7248 10.6756 11.3009 10.9882 10.9883C11.3007 10.6757 11.7246 10.5002 12.1667 10.5002H15.3333C15.7754 10.5002 16.1993 10.6757 16.5118 10.9883C16.8244 11.3009 17 11.7248 17 12.1668V15.3335C17 15.7755 16.8244 16.1994 16.5118 16.512C16.1993 16.8245 15.7754 17.0001 15.3333 17.0001H12.1667C11.7246 17.0001 11.3007 16.8245 10.9882 16.512C10.6756 16.1994 10.5 15.7755 10.5 15.3335V12.1668ZM12.1667 12.0002C12.1225 12.0002 12.0801 12.0178 12.0488 12.0491C12.0176 12.0803 12 12.1227 12 12.1668V15.3335C12 15.3776 12.0176 15.42 12.0488 15.4512C12.0801 15.4825 12.1225 15.5001 12.1667 15.5001H15.3333C15.3775 15.5001 15.4199 15.4825 15.4512 15.4512C15.4824 15.42 15.5 15.3776 15.5 15.3335V12.1668C15.5 12.1227 15.4824 12.0803 15.4512 12.0491C15.4199 12.0178 15.3775 12.0002 15.3333 12.0002H12.1667Z" fill="#212121"/>
</svg>
            </button>
          </div>
        </div>
        
     
      </div>

     
      {view === 'list'  && <label className="flex w-full max-w-[400px] items-center gap-2 rounded-[12px] border border-slate-100 bg-white px-2 py-2 text-slate-500 shadow-sm">
        <svg
          className="h-4 w-4 text-slate-300"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-3.5-3.5" />
        </svg>
        <input
          className="w-full bg-transparent text-[16px] font-medium text-slate-700 outline-none placeholder:text-slate-300"
          placeholder="Search Students"
           value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
        />
      </label>}

      {view === 'list' ? (
        <div className="overflow-hidden rounded-3xl bg-white">
          <div className="overflow-x-auto">
            <div className="min-w-[980px]">
              <div
                className={`grid ${listGridCols} gap-4 border-b border-slate-100 px-4 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400`}
              >
                <button
                  type="button"
                  onClick={() => onSort('name')}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold  text-slate-400 transition hover:text-slate-600"
                >
                  Student
                  <Icon
                    icon="mingcute:selector-vertical-line"
                    width={20}
                    height={20}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => onSort('status')}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold   text-slate-400 transition hover:text-slate-600"
                >
                  Status
                  <Icon
                    icon="mingcute:selector-vertical-line"
                    width={20}
                    height={20}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => onSort('focus')}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold   text-slate-400 transition hover:text-slate-600"
                >
                  Focus area
                  <Icon
                    icon="mingcute:selector-vertical-line"
                    width={20}
                    height={20}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => onSort('goal')}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold   text-slate-400 transition hover:text-slate-600"
                >
                  Goal
                  <Icon
                    icon="mingcute:selector-vertical-line"
                    width={20}
                    height={20}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => onSort('sessions')}
                  className="inline-flex items-center justify-center gap-1 text-[12px] font-semibold   text-slate-400 transition hover:text-slate-600"
                >
                  <span className="whitespace-nowrap">Total Sessions</span>
                  <Icon
                    icon="mingcute:selector-vertical-line"
                    width={20}
                    height={20}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => onSort('lastSession')}
                  className="inline-flex items-center gap-1 text-[12px] font-semibold   text-slate-400 transition hover:text-slate-600"
                >
                  Last Session
                  <Icon
                    icon="mingcute:selector-vertical-line"
                    width={20}
                    height={20}
                  />
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {isLoading
                  ? Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={`student-skeleton-${index}`}
                        className={`grid ${listGridCols} items-center gap-4 px-4 py-4`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-slate-100" />
                          <div className="space-y-2">
                            <div className="h-4 w-32 rounded-full bg-slate-100" />
                            <div className="h-3 w-24 rounded-full bg-slate-50" />
                          </div>
                        </div>
                        <div className="h-6 w-20 rounded-full bg-slate-100" />
                        <div className="h-4 w-28 rounded-full bg-slate-100" />
                        <div className="h-4 w-28 rounded-full bg-slate-100" />
                        <div className="h-4 w-10 rounded-full bg-slate-100" />
                        <div className="h-4 w-24 rounded-full bg-slate-100" />
                        <div className="flex justify-end gap-2">
                          <div className="h-8 w-8 rounded-xl bg-slate-100" />
                          <div className="h-8 w-8 rounded-xl bg-slate-100" />
                          <div className="h-8 w-8 rounded-xl bg-slate-100" />
                        </div>
                      </div>
                    ))
                  : sortedStudents.map((student, index) => {
                      const rowKey = student.id
                        ? `${student.id}-${index}`
                        : `row-${index}`;
                      const isSelected = student.id === studentId;
                      return (
                        <div
                          key={rowKey}
                          role="button"
                          tabIndex={0}
                          aria-label={`Select ${student.name}`}
                          aria-pressed={isSelected}
                          onClick={() => onSelectStudent(student.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onSelectStudent(student.id);
                            }
                          }}
                          className={`grid ${listGridCols} cursor-pointer items-center gap-4 px-4 py-4 text-sm text-slate-700 transition hover:bg-slate-50 ${
                            isSelected ? 'bg-slate-50' : ''
                          }`}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                              {student.initials}
                            </div>
                            <div>
                              <p className="font-semibold font-[14px] text-slate-900">
                                {student.name}
                              </p>
                            </div>
                          </div>
                          <div className="relative">
                            <button
                              type="button"
                              className={`w-fit rounded-[8px] px-3 py-2 text-xs font-semibold ${statusStyles[student.status]}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenStatusMenu(
                                  openStatusId === rowKey ? null : rowKey
                                );
                              }}
                              aria-haspopup="menu"
                              aria-expanded={openStatusId === rowKey}
                              disabled={!student.id || statusUpdating}
                            >
                              {student.status}
                            </button>
                            {openStatusId === rowKey ? (
                              <div
                                className="absolute left-0 top-full z-20 mt-2 w-44 rounded-2xl border border-slate-100 bg-white p-2 shadow-lg"
                                role="menu"
                                onClick={(event) => event.stopPropagation()}
                              >
                                {statusOptions.map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    className={`w-full rounded-xl px-3 py-2 text-left font-semibold font-[14px] ${option.text} hover:bg-slate-50`}
                                    onClick={() =>
                                      onStatusSelect(student, option.value)
                                    }
                                    disabled={updatingStatusId === student.id}
                                    role="menuitem"
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>
                            ) : null}
                          </div>
                          <span className="text-slate-600 font-semibold font-[14px] ">{student.focus}</span>
                          <div className="flex min-w-0 items-center gap-2 text-slate-600 font-semibold font-[14px]">
                            <span className="truncate">{student.goal}</span>
                            {student.goals.length > 1 ? (
                              <span className="text-xs font-semibold text-slate-400 font-semibold font-[14px]">
                                +{student.goals.length - 1} more
                              </span>
                            ) : null}
                          </div>
                          <span className="text-center font-semibold text-slate-900 tabular-nums">
                            {student.sessions}
                          </span>
                          <span className="text-slate-600 font-semibold font-[14px]">
                            {student.lastSession}
                          </span>
                          <div className="flex items-center justify-end gap-2 text-slate-600">
                            <button
                              className="flex  items-center justify-center   text-slate-900 transition hover:border-slate-300 hover:text-slate-900"
                              type="button"
                              aria-label="Open notes"
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenNotes(student.id);
                              }}
                            >
                              <svg
                                className="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M4 4h10l6 6v10a2 2 0 0 1-2 2H4z" />
                                <path d="M14 4v6h6" />
                              </svg>
                            </button>
                            <button
                              className="flex  items-center justify-center text-slate-900 transition hover:border-slate-300 hover:text-slate-900"
                              type="button"
                              aria-label="Open files"
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenResources(student.id);
                              }}
                            >
                              <svg
                                className="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M4 4h6l2 3h8v13H4z" />
                              </svg>
                            </button>
                            <button
                              className="flex items-center justify-center  text-slate-900 transition hover:border-slate-300 hover:text-slate-900"
                              type="button"
                              aria-label="Message"
                              onClick={(event) => {
                                event.stopPropagation();
                                onOpenMessages();
                              }}
                            >
                              <svg
                                className="h-4 w-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M21 15a4 4 0 0 1-4 4H7l-4 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                {hasError ? (
                  <div className="px-4 py-10 text-center text-sm text-rose-500">
                    Failed to load students.
                  </div>
                ) : null}
                {!isLoading && !hasError && students.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-slate-400">
                    No students found.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={isDetailView ? 'overflow-x-auto' : ''}>
          {hasError ? (
            <div className="rounded-2xl border border-dashed border-rose-200 bg-white px-4 py-10 text-center text-sm text-rose-500">
              Failed to load students.
            </div>
          ) : null}
          {isLoading ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-400">
              Loading students...
            </div>
          ) : null}
          {!isLoading && !hasError && students.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-400">
              No students found.
            </div>
          ) : null}
          <div
            className={`grid gap-4 ${
              isDetailView
                ? 'min-w-max grid-cols-[repeat(4,minmax(200px,_1fr))]'
                : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'
            }`}
          >
            {statusColumns.map((column) => {
              const columnStudents = sortedStudents.filter(
                (student) => student.status === column.status
              );
              return (
                <div
                  key={column.status}
                  className="flex min-h-[420px] w-[280px] flex-col gap-4 rounded-2xl border border-slate-100 p-4"
                  style={{ backgroundColor: column.panelColor }}
                  onDragOver={onDragOver}
                  onDrop={(event) => onDrop(column.status, event)}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-semibold ${column.text}`}>
                      {column.label}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${column.badge} ${column.text}`}
                    >
                      {columnStudents.length}
                    </span>
                  </div>
                  <div className="flex flex-col gap-3">
                    {columnStudents.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-200 bg-white/70 px-3 py-6 text-center text-xs text-slate-400">
                        Drop student here
                      </div>
                    ) : null}
                    {columnStudents.map((student) => (
                      <div
                        key={student.id}
                        draggable
                        onDragStart={(event) => onDragStart(student.id, event)}
                        onClick={() => onSelectStudent(student.id)}
                        className="cursor-pointer rounded-2xl  bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                            {student.initials}
                          </div>
                          <p className="text-sm font-semibold text-slate-900">
                            {student.name}
                          </p>
                        </div>
                        <p className="mt-3 text-xs text-slate-500">
                          {getStudentSummary(student.about)}
                        </p>
                        <button
                          className="mt-3 text-xs font-semibold text-slate-700"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelectStudent(student.id);
                          }}
                        >
                          See more
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsPanel;
